import React from 'react';

// Responsive wrapper for consistent spacing and layout
export const ResponsiveWrapper = ({ children, className = '', ...props }) => {
  return (
    <div className={`w-full px-2 sm:px-4 lg:px-6 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive grid wrapper
export const ResponsiveGrid = ({ children, className = '', cols = 1, ...props }) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  };

  return (
    <div className={`grid gap-4 sm:gap-6 ${gridCols[cols]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive flex wrapper
export const ResponsiveFlex = ({ children, className = '', direction = 'row', ...props }) => {
  const flexDirection = {
    row: 'flex-col sm:flex-row',
    col: 'flex-row sm:flex-col',
  };

  return (
    <div className={`flex ${flexDirection[direction]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive text wrapper
export const ResponsiveText = ({ children, size = 'base', className = '', ...props }) => {
  const textSizes = {
    xs: 'text-xs sm:text-sm',
    sm: 'text-sm sm:text-base',
    base: 'text-sm sm:text-base lg:text-lg',
    lg: 'text-base sm:text-lg lg:text-xl',
    xl: 'text-lg sm:text-xl lg:text-2xl',
    '2xl': 'text-xl sm:text-2xl lg:text-3xl',
  };

  return (
    <div className={`${textSizes[size]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive spacing wrapper
export const ResponsiveSpacing = ({ children, className = '', spacing = 'md', ...props }) => {
  const spacingClasses = {
    sm: 'space-y-2 sm:space-y-4',
    md: 'space-y-4 sm:space-y-6',
    lg: 'space-y-6 sm:space-y-8',
    xl: 'space-y-8 sm:space-y-12',
  };

  return (
    <div className={`${spacingClasses[spacing]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive button group
export const ResponsiveButtonGroup = ({ children, className = '', ...props }) => {
  return (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive card wrapper
export const ResponsiveCard = ({ children, className = '', padding = 'md', ...props }) => {
  const paddingClasses = {
    sm: 'p-3 sm:p-4',
    md: 'p-4 sm:p-6',
    lg: 'p-6 sm:p-8',
  };

  return (
    <div className={`card ${paddingClasses[padding]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive table wrapper - Updated to use new responsive table
export const ResponsiveTableWrapper = ({ children, className = '', ...props }) => {
  return (
    <div className={`overflow-x-auto ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive modal wrapper
export const ResponsiveModalWrapper = ({ children, className = '', ...props }) => {
  return (
    <div className={`w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive form wrapper
export const ResponsiveFormWrapper = ({ children, className = '', ...props }) => {
  return (
    <div className={`space-y-4 sm:space-y-6 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive form grid
export const ResponsiveFormGrid = ({ children, className = '', cols = 2, ...props }) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  };

  return (
    <div className={`grid gap-4 sm:gap-6 ${gridCols[cols]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive header wrapper
export const ResponsiveHeader = ({ children, className = '', ...props }) => {
  return (
    <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive actions wrapper
export const ResponsiveActions = ({ children, className = '', ...props }) => {
  return (
    <div className={`flex flex-wrap items-center gap-2 sm:gap-3 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive stats wrapper
export const ResponsiveStats = ({ children, className = '', ...props }) => {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive search wrapper
export const ResponsiveSearchWrapper = ({ children, className = '', ...props }) => {
  return (
    <div className={`w-full sm:w-auto ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive navigation wrapper
export const ResponsiveNavWrapper = ({ children, className = '', ...props }) => {
  return (
    <div className={`flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive content wrapper
export const ResponsiveContent = ({ children, className = '', ...props }) => {
  return (
    <div className={`w-full max-w-7xl mx-auto ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive sidebar content wrapper
export const ResponsiveSidebarContent = ({ children, className = '', ...props }) => {
  return (
    <div className={`flex-1 overflow-y-auto ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive footer wrapper
export const ResponsiveFooter = ({ children, className = '', ...props }) => {
  return (
    <div className={`flex flex-col sm:flex-row items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive list wrapper - New component for card-based lists
export const ResponsiveList = ({ children, className = '', variant = 'cards', ...props }) => {
  const variants = {
    cards: 'space-y-4',
    compact: 'space-y-3',
    grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
  };

  return (
    <div className={`${variants[variant]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive data display wrapper - New component for data presentation
export const ResponsiveDataDisplay = ({ children, className = '', layout = 'auto', ...props }) => {
  const layouts = {
    auto: 'block sm:flex sm:items-center sm:justify-between',
    stacked: 'flex flex-col space-y-2',
    inline: 'flex items-center space-x-4',
  };

  return (
    <div className={`${layouts[layout]} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive filter wrapper - New component for filters and search
export const ResponsiveFilterWrapper = ({ children, className = '', ...props }) => {
  return (
    <div className={`flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 ${className}`} {...props}>
      {children}
    </div>
  );
};

// Responsive pagination wrapper - New component for pagination
export const ResponsivePaginationWrapper = ({ children, className = '', ...props }) => {
  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 ${className}`} {...props}>
      {children}
    </div>
  );
};

export default {
  ResponsiveWrapper,
  ResponsiveGrid,
  ResponsiveFlex,
  ResponsiveText,
  ResponsiveSpacing,
  ResponsiveButtonGroup,
  ResponsiveCard,
  ResponsiveTableWrapper,
  ResponsiveModalWrapper,
  ResponsiveFormWrapper,
  ResponsiveFormGrid,
  ResponsiveHeader,
  ResponsiveActions,
  ResponsiveStats,
  ResponsiveSearchWrapper,
  ResponsiveNavWrapper,
  ResponsiveContent,
  ResponsiveSidebarContent,
  ResponsiveFooter,
  ResponsiveList,
  ResponsiveDataDisplay,
  ResponsiveFilterWrapper,
  ResponsivePaginationWrapper,
};
